import React, { useCallback, useState, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { createLowlight } from 'lowlight'
import { Dropcursor } from '@tiptap/extension-dropcursor'
import { Gapcursor } from '@tiptap/extension-gapcursor'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough,
  List, 
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Code,
  Code2,
  Table as TableIcon,
  Palette,
  Highlighter,
  Undo,
  Redo,
  Type,
  Heading1,
  Heading2,
  Heading3,
  Sparkles,
  CheckCircle,
  Eye,
  Edit3,
  Camera
} from 'lucide-react'
import { correctText, improveText } from '@/services/aiCorrection'
import { toast } from 'sonner'
import { useImageUpload } from '@/services/imageUploadService'

interface AdvancedRichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

interface MenuBarProps {
  editor: any
}

const MenuBar = ({ editor }: MenuBarProps) => {
  const [linkUrl, setLinkUrl] = useState('')
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [showImageInput, setShowImageInput] = useState(false)
  const [correcting, setCorrecting] = useState(false)
  const [improving, setImproving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const { uploadImage } = useImageUpload()
  const imageInputRef = useRef<HTMLInputElement>(null)

  if (!editor) {
    return null
  }

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run()
      setLinkUrl('')
      setShowLinkInput(false)
    }
  }

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run()
      setImageUrl('')
      setShowImageInput(false)
    }
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      const result = await uploadImage(file, 'articles')
      editor.chain().focus().setImage({ src: result.url }).run()
      toast.success('Image ajoutée à l\'article!')
    } catch (error: any) {
      toast.error('Erreur lors de l\'upload de l\'image')
    } finally {
      setUploading(false)
    }
  }

  const handleCorrection = async () => {
    setCorrecting(true)
    try {
      const content = editor.getText()
      const corrected = await correctText(content)
      editor.commands.setContent(corrected)
      toast.success('Texte corrigé avec succès!')
    } catch (error) {
      toast.error('Erreur lors de la correction')
    } finally {
      setCorrecting(false)
    }
  }

  const handleImprovement = async () => {
    setImproving(true)
    try {
      const content = editor.getText()
      const improved = await improveText(content)
      editor.commands.setContent(improved)
      toast.success('Suggestions d\'amélioration ajoutées!')
    } catch (error) {
      toast.error('Erreur lors de l\'amélioration')
    } finally {
      setImproving(false)
    }
  }

  return (
    <div className="border-b border-gray-200 bg-white p-3">
      <div className="flex flex-wrap gap-1 mb-3">
        {/* Undo/Redo */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
            className="h-8 w-8 p-0"
          >
            <Undo className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
            className="h-8 w-8 p-0"
          >
            <Redo className="w-4 h-4" />
          </Button>
        </div>

        {/* Headings */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading1 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading2 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading3 className="w-4 h-4" />
          </Button>
        </div>

        {/* Text formatting */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive('bold') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className="h-8 w-8 p-0"
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('italic') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className="h-8 w-8 p-0"
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('underline') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className="h-8 w-8 p-0"
          >
            <Underline className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('strike') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className="h-8 w-8 p-0"
          >
            <Strikethrough className="w-4 h-4" />
          </Button>
        </div>

        {/* Lists */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className="h-8 w-8 p-0"
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className="h-8 w-8 p-0"
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
        </div>

        {/* Alignment */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className="h-8 w-8 p-0"
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className="h-8 w-8 p-0"
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className="h-8 w-8 p-0"
          >
            <AlignRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Other formatting */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive('blockquote') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className="h-8 w-8 p-0"
          >
            <Quote className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('code') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleCode().run()}
            className="h-8 w-8 p-0"
          >
            <Code className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('codeBlock') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className="h-8 w-8 p-0"
          >
            <Code2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Link */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant={editor.isActive('link') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setShowLinkInput(!showLinkInput)}
            className="h-8 w-8 p-0"
          >
            <LinkIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Image */}
        <div className="flex gap-1 mr-3">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            className="h-8 w-8 p-0"
            title="Upload image depuis votre ordinateur"
          >
            <Camera className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowImageInput(!showImageInput)}
            className="h-8 w-8 p-0"
            title="Ajouter image par URL"
          >
            <ImageIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Table */}
        <div className="flex gap-1 mr-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            className="h-8 w-8 p-0"
          >
            <TableIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Colors */}
        <div className="flex gap-1 mr-3">
          <input
            type="color"
            onInput={(event: any) => editor.chain().focus().setColor(event.target.value).run()}
            value={editor.getAttributes('textStyle').color || '#000000'}
            className="w-8 h-8 rounded border cursor-pointer"
            title="Couleur du texte"
          />
          <Button
            type="button"
            variant={editor.isActive('highlight') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className="h-8 w-8 p-0"
          >
            <Highlighter className="w-4 h-4" />
          </Button>
        </div>

        {/* AI Tools */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCorrection}
            disabled={correcting}
            className="h-8 px-3"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {correcting ? 'Correction...' : 'Corriger'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleImprovement}
            disabled={improving}
            className="h-8 px-3"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {improving ? 'Amélioration...' : 'Améliorer'}
          </Button>
        </div>
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div className="flex gap-2 items-center mb-2 p-2 bg-gray-50 rounded">
          <Input
            type="url"
            placeholder="https://exemple.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && addLink()}
          />
          <Button type="button" size="sm" onClick={addLink}>
            Ajouter
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setShowLinkInput(false)}>
            Annuler
          </Button>
        </div>
      )}

      {/* Image Input */}
      {showImageInput && (
        <div className="flex gap-2 items-center mb-2 p-2 bg-gray-50 rounded">
          <Input
            type="url"
            placeholder="https://exemple.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && addImage()}
          />
          <Button type="button" size="sm" onClick={addImage}>
            Ajouter
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setShowImageInput(false)}>
            Annuler
          </Button>
        </div>
      )}
    </div>
  )
}

export const AdvancedRichTextEditor: React.FC<AdvancedRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Commencez à écrire votre article..."
}) => {
  const lowlight = createLowlight()
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg shadow-md',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-gray-300 w-full my-4',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border border-gray-300',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 bg-gray-100 p-2 font-semibold',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 p-2',
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'bg-yellow-200',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: {
          class: 'bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto',
        },
      }),
      Dropcursor,
      Gapcursor,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none p-6 focus:outline-none min-h-[400px] bg-white',
      },
    },
  })

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      
      {/* Status bar */}
      <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 text-sm text-gray-500 flex justify-between">
        <div>
          {editor?.storage.characterCount?.characters() || 0} caractères, {' '}
          {editor?.storage.characterCount?.words() || 0} mots
        </div>
        <div className="flex items-center gap-2">
          <Edit3 className="w-4 h-4" />
          Éditeur avancé
        </div>
      </div>
    </div>
  )
} 